import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { SettingsProvider } from "@/context/SettingsContext";
import PublicLayout from "@/components/layout/PublicLayout";
import { ProtectedRoute } from "@/components/layout/ProtectedRoute";
import Home from "@/pages/Home";
import { Login, Signup, ForgotPassword, ResetPassword } from "@/pages/auth/AuthPages";
import AccountLayout from "@/pages/account/AccountLayout";
import { AccountHome, AccountBookings, AccountBookingDetail, AccountInvoices, InvoiceView, AccountRewards, AccountProfile } from "@/pages/account/AccountPages";
const AdminBookings = lazy(() => import("@/pages/admin/AdminBookings"));
const AdminCalendar = lazy(() => import("@/pages/admin/AdminCalendar"));
const AdminCleaners = lazy(() => import("@/pages/admin/AdminOps").then((m) => ({ default: m.AdminCleaners })));
const AdminCleanerDetail = lazy(() => import("@/pages/admin/AdminOps").then((m) => ({ default: m.AdminCleanerDetail })));
const AdminCustomerDetail = lazy(() => import("@/pages/admin/AdminOps").then((m) => ({ default: m.AdminCustomerDetail })));
const AdminReviews = lazy(() => import("@/pages/admin/AdminOps").then((m) => ({ default: m.AdminReviews })));
const AdminInvoices = lazy(() => import("@/pages/admin/AdminOps").then((m) => ({ default: m.AdminInvoices })));
import AdminLayout, { ComingSoon } from "@/pages/admin/AdminLayout";
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminSettings = lazy(() => import("@/pages/admin/AdminSettings"));
const AdminPricing = lazy(() => import("@/pages/admin/AdminPricing"));
const AdminServiceAreas = lazy(() => import("@/pages/admin/AdminServiceAreas"));
const AdminPromotions = lazy(() => import("@/pages/admin/AdminPromotions"));
const AdminUsers = lazy(() => import("@/pages/admin/AdminUsers"));
const AdminPromoCodes = lazy(() => import("@/pages/admin/AdminPromoCodes"));
const AdminRewards = lazy(() => import("@/pages/admin/AdminRewards"));
const AdminContent = lazy(() => import("@/pages/admin/AdminContent"));
import Services from "@/pages/marketing/Services";
import Pricing from "@/pages/marketing/Pricing";
import { Offers, OfferDetail } from "@/pages/marketing/Offers";
import { ServiceAreas, ServiceAreaDetail } from "@/pages/marketing/ServiceAreas";
import { About, Contact, Faq } from "@/pages/marketing/InfoPages";
import BookingWizard from "@/pages/book/BookingWizard";
import BookingConfirmation from "@/pages/book/BookingConfirmation";
import GuestLookup from "@/pages/book/GuestLookup";
const AdminAvailability = lazy(() => import("@/pages/admin/AdminAvailability"));
const AdminAI = lazy(() => import("@/pages/admin/AdminAI"));
const AdminIntegrations = lazy(() => import("@/pages/admin/AdminIntegrations"));
import { ChatLauncher } from "@/components/support/ChatLauncher";

const Placeholder = null;

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SettingsProvider>
          <Routes>
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/services" element={<Services />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/offers" element={<Offers />} />
              <Route path="/offers/:slug" element={<OfferDetail />} />
              <Route path="/service-areas" element={<ServiceAreas />} />
              <Route path="/service-areas/:slug" element={<ServiceAreaDetail />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/faq" element={<Faq />} />
              <Route path="/book/confirmation/:id" element={<BookingConfirmation />} />
              <Route path="/guest/lookup" element={<GuestLookup />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
            </Route>
            <Route path="/account" element={<ProtectedRoute><AccountLayout /></ProtectedRoute>}>
              <Route index element={<AccountHome />} />
              <Route path="bookings" element={<AccountBookings />} />
              <Route path="bookings/:id" element={<AccountBookingDetail />} />
              <Route path="invoices" element={<AccountInvoices />} />
              <Route path="invoices/:id" element={<InvoiceView />} />
              <Route path="rewards" element={<AccountRewards />} />
              <Route path="profile" element={<AccountProfile />} />
            </Route>
            <Route path="/book" element={<BookingWizard />} />
            <Route path="/admin" element={<ProtectedRoute roles={["admin"]}><Suspense fallback={<div className="p-10 text-[#5A606A]">Loading…</div>}><AdminLayout /></Suspense></ProtectedRoute>}>
              <Route index element={<AdminDashboard />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="content" element={<AdminContent />} />
              <Route path="pricing" element={<AdminPricing />} />
              <Route path="service-areas" element={<AdminServiceAreas />} />
              <Route path="promotions" element={<AdminPromotions />} />
              <Route path="customers" element={<AdminUsers role="customer" title="Customers" />} />
              <Route path="cleaners" element={<AdminCleaners />} />
              <Route path="bookings" element={<AdminBookings />} />
              <Route path="calendar" element={<AdminCalendar />} />
              <Route path="customers/:id" element={<AdminCustomerDetail />} />
              <Route path="cleaners/:id" element={<AdminCleanerDetail />} />
              <Route path="invoices" element={<AdminInvoices />} />
              <Route path="reviews" element={<AdminReviews />} />
              <Route path="promo-codes" element={<AdminPromoCodes />} />
              <Route path="rewards" element={<AdminRewards />} />
              <Route path="availability" element={<AdminAvailability />} />
              <Route path="ai" element={<AdminAI />} />
              <Route path="integrations" element={<AdminIntegrations />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <ChatLauncher />
          <Toaster position="top-center" richColors />
        </SettingsProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
